import * as yup from 'yup';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

import { Input, Layout } from 'Components';
import { useStores } from 'Hooks/useStore';
import {
	ChangePasswordRequest,
	CognitoResponseError
} from 'Services/Api/Auth/Types';
import { EditableFieldsProps } from 'Components/EditableLayout/EditableFields';
import { useHistory } from 'react-router';
import { navigateBack } from 'Helpers/navigation';
import { getAppRoutes } from 'Pages/App/App.constants';
import NewPasswordFields from 'Pages/Auth/Login/Steps/NewPasswordFields/NewPasswordFields';
import { schema as passwordSchema } from 'Pages/Auth/Login/Steps/NewPasswordFields/schema';
import { VALIDATION } from 'Pages/Auth/AuthValidation';
import { COGNITO_ERROR_MSG_TO_DESCRIPTION_MAP } from 'app-config';
import { useDashboardContext } from 'Pages/Dashboard/DashboardContext';

const schema = yup
	.object()
	.shape({
		oldPassword: VALIDATION.password
	})
	.concat(passwordSchema);

interface Props {
	formikRef: React.Ref<FormikProps<ChangePasswordRequest>>;
}

function ChangePassword(props: EditableFieldsProps & Props) {
	const { t } = useTranslation();
	const history = useHistory();
	const { authStore } = useStores();
	const { setToastInfo } = useDashboardContext();
	const initialValues: ChangePasswordRequest = {
		oldPassword: '',
		password: '',
		confirmPassword: ''
	};

	const onSubmit = async (
		values: ChangePasswordRequest,
		helpers: FormikHelpers<ChangePasswordRequest>
	) => {
		try {
			await authStore.changePassword(values);
			helpers.resetForm({ values });
			navigateBack(history, getAppRoutes().HOME);
			setToastInfo?.({
				message: t('notification.edit.changePassword'),
				type: 'success',
				icon: 'check'
			});
		} catch (e) {
			const error = e as CognitoResponseError;
			// reset to initial for prompt not blocking transition
			helpers.setSubmitting(false);

			if (error.code === 'NotAuthorizedException') {
				const errors: Partial<
					Record<keyof ChangePasswordRequest, string>
				> = {
					oldPassword: t(
						'user.editUser.tabs.changePasswordTab.oldPasswordIncorrect'
					)
				};
				helpers.setErrors(errors);
				return;
			}
			const cognitoError =
				COGNITO_ERROR_MSG_TO_DESCRIPTION_MAP[error.message];
			const description =
				cognitoError ?? 'errorCodes.genericErrorMessage';

			setToastInfo?.({ message: t(description), type: 'warning' });
		}
	};

	return (
		<Formik
			innerRef={props.formikRef}
			initialValues={initialValues}
			onSubmit={onSubmit}
			validationSchema={schema}
		>
			<Layout.List.Section>
				<Input.PasswordFormikField
					name="oldPassword"
					label={t(
						'user.editUser.tabs.changePasswordTab.oldPassword'
					)}
					placeholder={t(
						'user.editUser.tabs.changePasswordTab.oldPasswordPlaceholder'
					)}
					required
				/>
				<NewPasswordFields newDesign={true} />
			</Layout.List.Section>
		</Formik>
	);
}

export default ChangePassword;
